import request from "project-config/services/request/request";
import { SearchOutlined } from "@ant-design/icons";
import { Dropdown, Input, Tooltip } from "@renderbus/ui";
import { Empty } from "antd";
import { useEffect, useRef, useState } from "react";
import styled, { createGlobalStyle } from "styled-components";
import DepartmentIcon from "./icons/department";
import NameIcon from "./icons/name";
import { useSubAccountStore } from "@/store/SubAccountStore";
import { useDingTalkSubAccountStore } from "../../store/dingtalk-subaccount-store";
import { useTranslation } from "react-i18next";
import { useDingTalkChildManage } from "../../hooks/useDingTalkChildManage";
const DropdownItem = styled.div`
  width: 199px;
  height: 70px;
  border-radius: 2px;
  background: #222227;
  margin-bottom: 6px;
  cursor: pointer;
  padding: 6px;
  font-size: 12px;
  .company {
    color: rgba(126, 145, 154, 1);
  }
  :hover {
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.24);
  }
`;
const DropdownBottom = styled.div`
  text-align: center;
  font-size: 12px;
  letter-spacing: 0px;
  color: rgba(93, 105, 112, 1);
  margin-top: 15px;
`;

const DropdownStyle = createGlobalStyle`
.ant-dropdown{
  background: #343538;
  padding:12px 8px 5px;
  border-radius: 4px;
  box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.24);
  overflow: scroll;
  max-height:310px;
  .ant-dropdown-menu {
    background: #343538;
    border: 1px solid #515356;
  }
  .ant-empty-description {
    color: #fff;
  }
}
.renderbus-ui--dropdown  {
  width:215px;
}
.input-select-dropdown{
}
`;
const UserInfo = styled.div`
  color: rgba(224, 236, 242, 1);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
function findObjectWithDeptId(array: any[], targetDeptId: number): any | null {
  for (let i = 0; i < array.length; i++) {
    const obj = array[i];
    if (obj.deptId === targetDeptId) {
      return obj;
    }
    if (obj.deptList && obj.deptList.length > 0) {
      const nestedObject = findObjectWithDeptId(obj.deptList, targetDeptId);
      if (nestedObject) {
        return nestedObject;
      }
    }
  }
  return null;
}
function findObjectWithAncestors(array: any[], targetDeptId: number): any[] {
  const result: any[] = [];

  function findObjectRecursive(obj: any, ancestors: any[]) {
    if (obj.deptId === targetDeptId) {
      result.push(...ancestors, obj);
      return;
    }
    if (obj.deptList && obj.deptList.length > 0) {
      const updatedAncestors = [...ancestors, obj];
      for (const nestedObj of obj.deptList) {
        findObjectRecursive(nestedObj, updatedAncestors);
      }
    }
  }
  for (const obj of array) {
    findObjectRecursive(obj, []);
  }
  return result;
}
type DeptProp = {
  deptId: number;
  deptOrg: string;
};
type SubAccountProp = {
  key?: string;
  subUserId: number;
  subUserName: string;
  company: string;
  keyIndex: number;
} & DeptProp;
const InputSelect: React.FC = () => {
  const { t } = useTranslation("childManager");
  const [searchText, setSearchText] = useState<any>("");
  const [searchResults, setSearchResults] = useState<SubAccountProp[]>([]);
  const [open, setOpen] = useState(false);
  const { deptInfo } = useDingTalkChildManage();
  const { fetchDingTalkSubAccountList, setIsSearchSubUser } =
    useSubAccountStore();
  const {
    queryDeptList,
    setSelectTreeKey,
    setExpandedDeptyKeys,
    expandeDeptyKeys,
  } = useDingTalkSubAccountStore();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const handleSearch = async (value: string) => {
    const { data } = await request.post(
      "/api/rendering/user/queryDingTalkSubUserRelevanceDeptList",
      { subUserName: value }
    );
    const result: SubAccountProp[] = [];
    for (let i = 0; i < data.length; i++) {
      data[i].deptList.map((item: any) => {
        const org = item.deptOrg.split("-");
        const dept = org.slice(1).join("-");
        result.push({
          key: data[i].subUserId.toString() + item.deptId.toString(),
          subUserId: data[i].subUserId,
          subUserName: data[i].subUserName,
          deptId: item.deptId,
          deptOrg: dept,
          company: org[0] || "",
          keyIndex: item.keyIndex,
        });
        return item;
      });
    }
    setSearchResults(result);
  };
  const onItemClick = (option: SubAccountProp) => {
    setIsSearchSubUser(true);
    fetchDingTalkSubAccountList({
      deptId: option.deptId,
      subUserId: option.subUserId,
    });
    const targetDept = findObjectWithDeptId(deptInfo, option.deptId);
    const ancestors = findObjectWithAncestors(deptInfo, option.deptId);
    setSelectTreeKey(targetDept.keyIndex);
    setExpandedDeptyKeys([
      ...expandeDeptyKeys,
      ...ancestors.map((item) => item.keyIndex),
    ]);
    setOpen(false);
  };
  const onChange = (e: any) => {
    setSearchText(e.target.value);
    handleSearch(e.target.value);
  };
  useEffect(() => {
    if (!searchText) {
      setIsSearchSubUser(false);
      setSearchResults([]);
      queryDeptList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleClickOutside = (event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node)
    ) {
      setOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  return (
    <div ref={dropdownRef} style={{ display: "inline-block" }}>
      <Dropdown
        open={open}
        overlayClassName="input-select-dropdown"
        dropdownRender={() => (
          <>
            {searchResults.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <>
                {searchResults.map((item) => (
                  <DropdownItem
                    key={item.key}
                    onClick={() => onItemClick(item)}
                  >
                    <Tooltip
                      placement="top"
                      title={item.subUserName}
                      color={"#202124"}
                      tooltipText={
                        <UserInfo>
                          <NameIcon style={{ marginRight: 3 }} />
                          {item.subUserName}
                        </UserInfo>
                      }
                    />
                    <Tooltip
                      placement="top"
                      title={item.deptOrg}
                      color={"#202124"}
                      tooltipText={
                        <UserInfo>
                          <DepartmentIcon style={{ marginRight: 3 }} />
                          {item.deptOrg}
                        </UserInfo>
                      }
                    />
                    <Tooltip
                      placement="top"
                      title={item.company}
                      color={"#202124"}
                      tooltipText={
                        <UserInfo className="company">{item.company}</UserInfo>
                      }
                    />
                  </DropdownItem>
                ))}
                <DropdownBottom>
                  {t("dingdng_subaccount_has_all")}
                </DropdownBottom>
              </>
            )}
          </>
        )}
      >
        <Input
          value={searchText}
          onChange={onChange}
          onPressEnter={() => handleSearch(searchText)}
          suffix={<SearchOutlined />}
          allowClear
          onFocus={() => setOpen(true)}
        />
      </Dropdown>
      <DropdownStyle />
    </div>
  );
};

export default InputSelect;
